/**
 * Shared booking days utility.
 * Single source of truth for relative-day count, human-readable labels,
 * and badge color styles used across all booking views.
 */

/**
 * Returns the number of calendar days between today and the given date string.
 * Positive = future, 0 = today, negative = past.
 * Uses local midnight comparison so the result matches wall-clock expectations.
 */
export function getDaysAway(dateStr: string | null | undefined): number {
  if (!dateStr) return 9999;
  const [y, m, d] = dateStr.split("-").map(Number);
  if (!y || !m || !d) return 9999;
  const target = new Date(y, m - 1, d);
  target.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / 86_400_000);
}

/**
 * Converts a day-count from getDaysAway into a concise human label.
 *
 * Future tiers:
 *   0        → "Today"
 *   1        → "Tomorrow"
 *   2–6      → "2 days" … "6 days"
 *   7–13     → "1 wk"
 *   14–20    → "2 wks"
 *   21–27    → "3 wks"
 *   28+      → "1 mo", "2 mo", …
 *
 * Past tiers (days < 0):
 *   -1 to -6     → "1 day ago" … "6 days ago"
 *   -7 to -13    → "1 wk ago"
 *   -14 to -20   → "2 wks ago"
 *   -21+         → "X mo ago"
 */
export function getRelativeLabel(days: number): string {
  if (days >= 9999) return "Pending";
  if (days === 0) return "Today";
  if (days === 1) return "Tomorrow";

  if (days > 0) {
    if (days <= 6) return `${days} days`;
    if (days <= 13) return "1 wk";
    if (days <= 20) return "2 wks";
    if (days <= 27) return "3 wks";
    const months = Math.round(days / 30);
    return `${Math.max(1, months)} mo`;
  }

  const abs = Math.abs(days);
  if (abs === 1) return "1 day ago";
  if (abs <= 6) return `${abs} days ago`;
  if (abs <= 13) return "1 wk ago";
  if (abs <= 20) return "2 wks ago";
  if (abs <= 27) return "3 wks ago";
  const months = Math.round(abs / 30);
  return `${Math.max(1, months)} mo ago`;
}

/**
 * Returns inline-style values for a pill badge based on day-count.
 *
 * Scale:
 *   Today (0)   — red/urgent
 *   1–2 days    — orange/amber
 *   3–7 days    — yellow
 *   8–14 days   — teal/blue
 *   15+ days    — green/muted
 *   Past (< 0)  — gray/dim
 */
export function getRelativeBadgeStyle(days: number): {
  color: string;
  background: string;
  border: string;
} {
  let hex: string;

  if (days >= 9999) {
    hex = "#f59e0b";
  } else if (days < 0) {
    hex = "#6b7280";
  } else if (days === 0) {
    hex = "#ef4444";
  } else if (days <= 2) {
    hex = "#f97316";
  } else if (days <= 7) {
    hex = "#eab308";
  } else if (days <= 14) {
    hex = "#0d9488";
  } else {
    hex = "#22c55e";
  }

  return {
    color: hex,
    background: `${hex}22`,
    border: `1px solid ${hex}44`,
  };
}
